import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { TableUserItem } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { OrganisationQuery, useCancelInviteMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { getRoleLabel } from 'src/api/types';

type Invite = NonNullable<OrganisationQuery['organisation']>['invites'][number];

type UseColumns = (a: { organisation: NonNullable<OrganisationQuery['organisation']> }) => {
  columns: ColumnDef<Invite>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['name', 'email', 'role'];

export const useInvitesColumns: UseColumns = ({ organisation }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.orgInvites}-columns`, defaultColumns);
  const { refetchQuery } = useCache();

  const [cancelInvite] = useCancelInviteMutation({
    onCompleted: () => refetchQuery({ include: ['profile', 'organisation'] }),
  });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<Invite>[] = [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: ({ row }) => {
          return <TableUserItem name={row.original.name || ''} profilePhotoPath={row.original.profilePhotoPath} />;
        },
        sortingFn: sortingFns.text,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'role',
        header: 'Role',
        cell: ({ row }) => getRoleLabel(row.original.invite?.invitedRole),
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const canInviteUsers = organisation.actionsOnThisOrganisation?.canInviteUsers;

          const onCancelInviteClick = canInviteUsers
            ? () => cancelInvite({ variables: { inviteId: row.original.invite?.id! } })
            : undefined;

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm onCancelInviteClick={onCancelInviteClick} />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [organisation.invites, selectedColumns, cancelInvite, organisation.actionsOnThisOrganisation?.canInviteUsers]);

  console.log('organisation.invites', organisation.invites);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.orgInvites}
      options={[
        { key: 'name', title: 'Name' },
        { key: 'email', title: 'Email' },
        { key: 'role', title: 'Role' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
